import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

interface TimelineEvent {
  id?: string;
  title: string;
  target_date: string;
  location?: string;
  who_with?: string;
  what_doing?: string;
  feeling_state?: string;
  thoughts_about_self?: string;
  thoughts_about_others?: string;
  how_others_see_me?: string;
  meaning?: string;
}

interface TimelineEventDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (event: TimelineEvent) => void;
  initialData?: TimelineEvent;
}

export default function TimelineEventDialog({
  open,
  onClose,
  onSave,
  initialData
}: TimelineEventDialogProps) {
  const [formData, setFormData] = useState<TimelineEvent>({
    title: '',
    target_date: '',
    location: '',
    who_with: '',
    what_doing: '',
    feeling_state: '',
    thoughts_about_self: '',
    thoughts_about_others: '',
    how_others_see_me: '',
    meaning: ''
  });

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    } else {
      setFormData({
        title: '',
        target_date: '',
        location: '',
        who_with: '',
        what_doing: '',
        feeling_state: '',
        thoughts_about_self: '',
        thoughts_about_others: '',
        how_others_see_me: '',
        meaning: ''
      });
    }
  }, [initialData, open]);

  const handleSubmit = () => {
    if (formData.title && formData.target_date) {
      onSave(formData);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {initialData ? 'Edit Timeline Destination' : 'Add Timeline Destination'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="title">Destination Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Living in my dream home"
            />
          </div>

          <div>
            <Label htmlFor="target_date">Target Date *</Label>
            <Input
              id="target_date"
              type="date"
              value={formData.target_date}
              onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              value={formData.location || ''}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="Where are you?"
            />
          </div>

          <div>
            <Label htmlFor="who_with">Who With</Label>
            <Input
              id="who_with"
              value={formData.who_with || ''}
              onChange={(e) => setFormData({ ...formData, who_with: e.target.value })}
              placeholder="Who are you with?"
            />
          </div>

          <div>
            <Label htmlFor="what_doing">What Doing</Label>
            <Textarea
              id="what_doing"
              value={formData.what_doing || ''}
              onChange={(e) => setFormData({ ...formData, what_doing: e.target.value })}
              placeholder="What are you doing?"
            />
          </div>

          <div>
            <Label htmlFor="feeling_state">Feeling State</Label>
            <Input
              id="feeling_state"
              value={formData.feeling_state || ''}
              onChange={(e) => setFormData({ ...formData, feeling_state: e.target.value })}
              placeholder="How do you feel?"
            />
          </div>

          <div>
            <Label htmlFor="meaning">Meaning</Label>
            <Textarea
              id="meaning"
              value={formData.meaning || ''}
              onChange={(e) => setFormData({ ...formData, meaning: e.target.value })}
              placeholder="What does this mean to you?"
            />
          </div>

          <div className="flex gap-2 justify-end pt-4">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!formData.title || !formData.target_date}>
              {initialData ? 'Update' : 'Create'} Destination
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
